/**
 * STRUCT CODE v2.0 Dynamic API Service
 * 動的構造計算APIとの通信
 */

import axios from 'axios';

// 注意: REACT_APP_API_V2_URL はネイタル用なので使用しない。
// 以前は本番 URL をハードコードしており、ローカル開発でも本番 DB に
// 書き込んでいた。他のサービスと同じく REACT_APP_API_URL を基準にする。
const API_ORIGIN = process.env.REACT_APP_API_URL || 'https://struct-code-production.up.railway.app';
const API_BASE_URL = `${API_ORIGIN}/api/v2/dynamic`;

class DynamicStructCodeService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 180000, // 動的計算は時間がかかる可能性があるため3分に設定
    });
  }

  /**
   * ヘルスチェック
   */
  async healthCheck() {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error) {
      console.error('Dynamic health check failed:', error);
      throw this._handleError(error);
    }
  }

  /**
   * 動的診断を実行
   * @param {Object} diagnosisData - 診断データ
   * @param {string} diagnosisData.birth_date - 生年月日 (YYYY-MM-DD)
   * @param {string} diagnosisData.birth_location - 出生地
   * @param {Array} diagnosisData.answers - 25問の回答
   * @param {string} [diagnosisData.birth_time] - 出生時刻 (HH:MM) 任意
   * @param {string} [diagnosisData.diagnosis_date] - 診断日時 (ISO形式) 任意
   * @returns {Promise<Object>} 動的診断結果
   */
  async createDynamicDiagnosis(diagnosisData) {
    try {
      const headers = {};
      const token = localStorage.getItem('struct-code-token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await this.client.post('/diagnosis', diagnosisData, { headers });
      return response.data;
    } catch (error) {
      console.error('Dynamic diagnosis creation failed:', error);
      throw this._handleError(error);
    }
  }

  /**
   * 診断結果を取得
   * @param {string} diagnosisId - 診断ID
   * @returns {Promise<Object>} 診断結果
   */
  async getDiagnosis(diagnosisId) {
    try {
      const response = await this.client.get(`/diagnosis/${diagnosisId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get dynamic diagnosis:', error);
      throw this._handleError(error);
    }
  }

  /**
   * 時期比較を実行
   * @param {Object} diagnosisData - 診断データ
   * @returns {Promise<Object>} 比較結果（半年前、現在、半年後）
   */
  async compareTimePeriods(diagnosisData) {
    try {
      const response = await this.client.post('/compare', diagnosisData);
      return response.data;
    } catch (error) {
      console.error('Time comparison failed:', error);
      throw this._handleError(error);
    }
  }

  /**
   * 個人AI行動特性分析のキャッシュを取得
   * @param {string} diagnosisId - 診断ID
   * @returns {Promise<Object|null>} AI分析結果 (null if not yet generated)
   */
  async getCachedPersonalAiAnalysis(diagnosisId) {
    try {
      const response = await this.client.get(`/diagnosis/${diagnosisId}/ai-analysis`);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return null; // Not yet generated
      }
      console.error('Failed to get personal AI analysis:', error);
      throw this._handleError(error);
    }
  }

  /**
   * 個人AI行動特性分析を生成（または再生成）
   * @param {string} diagnosisId - 診断ID
   * @returns {Promise<Object>} AI分析結果
   */
  async generatePersonalAiAnalysis(diagnosisId) {
    try {
      const response = await this.client.post(
        `/diagnosis/${diagnosisId}/ai-analysis`,
        {},
        { timeout: 90000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to generate personal AI analysis:', error);
      throw this._handleError(error);
    }
  }

  /**
   * エラーハンドリング
   * @private
   */
  _handleError(error) {
    if (error.response) {
      const { status, data } = error.response;
      return {
        message: data.detail || data.message || 'サーバーエラーが発生しました',
        status,
        data,
      };
    } else if (error.request) {
      return {
        message: 'サーバーに接続できません。ネットワーク接続を確認してください。',
        status: 0,
      };
    } else {
      return {
        message: error.message || '予期しないエラーが発生しました',
        status: -1,
      };
    }
  }

  /**
   * 動的診断結果を解析
   * @param {Object} result - 動的診断結果
   * @returns {Object} 解析された結果
   */
  analyzeResult(result) {
    if (!result) return null;

    const { natal, current, design_gap, temporal } = result;

    // タイプが変化したかどうか
    const typeChanged = natal?.type !== current?.type;

    // 最も変化した軸を特定
    let maxChange = { axis: null, value: 0 };
    if (design_gap) {
      Object.entries(design_gap).forEach(([axis, value]) => {
        if (Math.abs(value) > Math.abs(maxChange.value)) {
          maxChange = { axis, value };
        }
      });
    }

    // 活性化/抑制されている軸を分類
    const activated = [];
    const suppressed = [];
    const stable = [];

    if (design_gap) {
      Object.entries(design_gap).forEach(([axis, value]) => {
        if (value > 0.05) activated.push(axis);
        else if (value < -0.05) suppressed.push(axis);
        else stable.push(axis);
      });
    }

    return {
      typeChanged,
      maxChange,
      activated,
      suppressed,
      stable,
      currentTheme: temporal?.current_theme || '安定した時期',
      futureOutlook: temporal?.future_outlook || [],
    };
  }

  /**
   * DesignGapの説明文を生成
   * @param {Object} designGap - DesignGap
   * @returns {string} 説明文
   */
  generateGapExplanation(designGap) {
    if (!designGap) return '';

    const explanations = [];

    Object.entries(designGap).forEach(([axis, value]) => {
      const percent = Math.round(value * 100);
      if (value > 0.1) {
        explanations.push(`${axis}が${percent}%活性化しています`);
      } else if (value < -0.1) {
        explanations.push(`${axis}が${Math.abs(percent)}%抑制されています`);
      }
    });

    if (explanations.length === 0) {
      return '全体的にネイタル構造に近い安定した状態です。';
    }

    return explanations.join('。') + '。';
  }
}

// シングルトンインスタンスをエクスポート
const dynamicStructCodeService = new DynamicStructCodeService();
export default dynamicStructCodeService;
